// src/pages/admin/ManageRankings.tsx
import React, { useState, useEffect, useCallback } from "react";
import Navbar from "../../components/Navbar";
import { useTranslation } from "../../hooks/useTranslation";
import { C, FONT } from "../../theme";
import {
  getRankingCompetitions,
  addRankingCompetition,
  updateRankingCompetition,
  deleteRankingCompetition,
  saveRankingResult,
  updateRankingResult,
  deleteRankingResult,
  getResultsByCompetition,
  recalculateAndSaveSnapshots,
  RankingCompetition,
  RankingResult,
} from "../../firebase/rankings";
import { getAthletesAggregate } from "../../firebase/athletes";
import { addClub } from "../../firebase/clubs";
import { getAllClubs as getClubs } from "../../firebase/clubs";
import { Timestamp, collection, query, where, getDocs, getDoc, doc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { Link } from "react-router-dom";
import {
  calculatePoints,
  getDefaultPointsForType,
  COMPETITION_TYPE_LABELS,
  AGE_GROUP_DEFINITIONS,
  getWeightClassesForGroup,
  PLACE_RANGES,
  placeLabel,
} from "../../utils/rankingCalculator";

// ── Responsive hook ──────────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

// ── Helpers ──────────────────────────────────────────────────────────────────
const toDateStr = (ts: any): string => {
  if (!ts) return "";
  // Check for Firestore Timestamp
  if (ts && typeof (ts as any).toDate === "function") return ts.toDate().toLocaleDateString("en-GB");
  // Check for native Date
  if (ts instanceof Date) return ts.toLocaleDateString("en-GB");
  return "";
};

const tsToInputVal = (ts: any): string => {
  if (!ts) return "";
  if (ts && typeof (ts as any).toDate === "function") return ts.toDate().toISOString().split("T")[0];
  if (ts instanceof Date) return ts.toISOString().split("T")[0];
  return "";
};

const COMP_TYPES = Object.keys(COMPETITION_TYPE_LABELS);

const GENDER_OPTIONS = [
  { value: "male",        labelKey: "admin.rankings.results.genderMale" },
  { value: "female",      labelKey: "admin.rankings.results.genderFemale" },

];

// ── Birth year check ─────────────────────────────────────────────────────────
// Returns a warning string if DOB and age group may mismatch — non-blocking.
const AGE_GROUP_YEAR_RANGES: Record<string, [number, number]> = {
  Kids:    [2017, 2019],
  Cadets:  [2012, 2014],
  Juniors: [2009, 2011],
  U21:     [2006, 2010],
  Seniors: [1900, 2009],
 
};

function dobAgeGroupWarning(dob: string | undefined, ageGroup: string): string {
  if (!dob || !ageGroup) return "";
  const birthYear = new Date(dob).getFullYear();
  if (isNaN(birthYear)) return "";
  const range = AGE_GROUP_YEAR_RANGES[ageGroup];
  if (!range) return "";
  if (birthYear < range[0] || birthYear > range[1]) {
    return `⚠️ Birth year ${birthYear} may not match ${ageGroup} (${range[0]}–${range[1]}). You can still save.`;
  }
  return "";
}

// ── Blank forms ──────────────────────────────────────────────────────────────
const blankComp = (): Partial<RankingCompetition> & { dateStr: string } => ({
  name: "", nameKa: "", type: "georgian_championship", dateStr: "",
  location: "", totalPoints: 32, participationPoints: 4, pointsPerWin: 2, isActive: true,
});

const DEFAULT_AGE_GROUP = "Seniors";
const DEFAULT_GENDER = "male";

const blankResultForm = () => ({
  gender: DEFAULT_GENDER,
  ageGroup: DEFAULT_AGE_GROUP,
  weightClass: getWeightClassesForGroup(DEFAULT_AGE_GROUP, DEFAULT_GENDER)[0],
  clubName: "",
  // placeValue: canonical tier value (1,2,3,5,9,17) or "" for participated-only
  placeValue: "" as string | number,
  participated: false,
  wins: 0,
  overridePoints: false,
  pointsManual: 0,
});

// ── Component ────────────────────────────────────────────────────────────────
const ManageRankings: React.FC = () => {
  const { t } = useTranslation();
  const w = useWindowWidth();
  const isMobile = w < 768;

  const [activeTab, setActiveTab] = useState<"competitions" | "results" | "publish">("competitions");

  // Tab 1
  const [competitions, setCompetitions] = useState<any[]>([]);
  const [loadingComps, setLoadingComps] = useState(true);
  const [showCompModal, setShowCompModal] = useState(false);
  const [editingComp, setEditingComp] = useState<RankingCompetition | null>(null);
  const [compForm, setCompForm] = useState(blankComp());
  const [compError, setCompError] = useState("");
  const [compSaving, setCompSaving] = useState(false);
  const [deleteCompId, setDeleteCompId] = useState<string | null>(null);
  const [compSearch, setCompSearch] = useState("");
  const [compSearchResults, setCompSearchResults] = useState(""); // search term for competition selector (Tab 2)

  // Tab 2
  const [athletes, setAthletes] = useState<any[]>([]);
  const [clubs, setClubs] = useState<string[]>([]);
  const [selectedCompId, setSelectedCompId] = useState("");
  const [compResults, setCompResults] = useState<RankingResult[]>([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [athleteSearch, setAthleteSearch] = useState("");
  const [selectedAthlete, setSelectedAthlete] = useState<any | null>(null);
  const [resultForm, setResultForm] = useState(blankResultForm());
  const [autoPoints, setAutoPoints] = useState(0);
  const [resultSaving, setResultSaving] = useState(false);
  const [resultError, setResultError] = useState("");
  const [editingResult, setEditingResult] = useState<RankingResult | null>(null);
  const [clubFreeText, setClubFreeText] = useState(false); // toggle between dropdown and free-text
  const [resultsSearch, setResultsSearch] = useState(""); // search/filter for the results table

  // Tab 3
  const [publishYear, setPublishYear] = useState(new Date().getFullYear());
  const [publishing, setPublishing] = useState(false);
  const [publishMsg, setPublishMsg] = useState("");
  const [lastPublished, setLastPublished] = useState<string | null>(null);

  // ── Publish preview modal ────────────────────────────────────────────────
  interface PublishPreview {
    year: number;
    snapshotsToWrite: number;     // unique (gender, ageGroup, weightClass) groups
    athletesAffected: number;     // unique athleteIds in the year
    resultRows: number;           // total ranking-result rows
    existingSnapshots: number;    // how many docs for that year already exist
    lastPublishedAt: string | null; // from the most recent existing snapshot
  }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState<PublishPreview | null>(null);
  const [previewError, setPreviewError] = useState("");

  // ── Load data ────────────────────────────────────────────────────────────
  const loadComps = useCallback(async () => {
    setLoadingComps(true);
    try {
      setCompetitions(await getRankingCompetitions());
    } catch { /* silent */ } finally {
      setLoadingComps(false);
    }
  }, []);

  useEffect(() => {
    loadComps();
    getAthletesAggregate().then((a) => setAthletes(a as any[])).catch(() => {});
    getClubs().then((cl: any[]) => setClubs(cl.map((c: any) => c.name || c))).catch(() => {});
  }, [loadComps]);

  useEffect(() => {
    if (!selectedCompId) { setCompResults([]); return; }
    setLoadingResults(true);
    getResultsByCompetition(selectedCompId)
      .then(setCompResults).catch(() => {})
      .finally(() => setLoadingResults(false));
  }, [selectedCompId]);

  // ── Auto-calculate points whenever relevant form fields change ────────────
  useEffect(() => {
    const selectedComp = competitions.find((c) => c.id === selectedCompId);
    const compType = selectedComp?.type ?? "georgian_championship";
    const place = resultForm.participated ? null : (resultForm.placeValue !== "" ? Number(resultForm.placeValue) : null);
    // Pass competition's own participationPoints so the auto value reflects the real formula
    const pts = calculatePoints(compType, place, resultForm.wins, undefined, selectedComp?.participationPoints);
    setAutoPoints(pts);
  }, [resultForm.placeValue, resultForm.participated, resultForm.wins, selectedCompId, competitions]);

  // ── Weight class auto-reset when gender or ageGroup changes ──────────────
  useEffect(() => {
    const opts = getWeightClassesForGroup(resultForm.ageGroup, resultForm.gender);
    if (!opts.includes(resultForm.weightClass)) {
      setResultForm((prev) => ({ ...prev, weightClass: opts[0] ?? "" }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultForm.gender, resultForm.ageGroup]);

  // ── Competition type auto-fill ───────────────────────────────────────────
  const handleCompTypeChange = (type: string) => {
    const defaults = getDefaultPointsForType(type);
    setCompForm((prev) => ({ ...prev, type, totalPoints: defaults.totalPoints, participationPoints: defaults.participationPoints, pointsPerWin: defaults.pointsPerWin }));
  };

  // ── Save competition ─────────────────────────────────────────────────────
  const handleSaveComp = async () => {
    if (!compForm.name || !compForm.dateStr) { setCompError("Name and date are required."); return; }
    setCompSaving(true); setCompError("");
    try {
      const dateObj = new Date(compForm.dateStr);
      const payload: Omit<RankingCompetition, "id"> = {
        name: compForm.name!, nameKa: compForm.nameKa ?? "", type: compForm.type!,
        date: Timestamp.fromDate(dateObj), year: dateObj.getFullYear(), month: dateObj.getMonth() + 1,
        location: compForm.location ?? "", totalPoints: Number(compForm.totalPoints),
        participationPoints: Number(compForm.participationPoints), pointsPerWin: Number(compForm.pointsPerWin), isActive: true,
      };
      if (editingComp?.id) { await updateRankingCompetition(editingComp.id, payload); }
      else { await addRankingCompetition(payload); }
      setShowCompModal(false); setEditingComp(null); setCompForm(blankComp()); loadComps();
    } catch { setCompError("Failed to save competition."); }
    finally { setCompSaving(false); }
  };

  // ── Save result ──────────────────────────────────────────────────────────
  const handleSaveResult = async () => {
    if (!selectedCompId) { setResultError("Select a competition first."); return; }
    if (!selectedAthlete) { setResultError("Select an athlete."); return; }
    if (!resultForm.weightClass) { setResultError("Select a weight class."); return; }
    if (!resultForm.clubName.trim()) { setResultError("Enter a club name."); return; }

    // ── Ensure club exists ──────────────────────────────────────────────
    if (!clubs.includes(resultForm.clubName.trim())) {
      const shouldCreate = window.confirm(
        `Club "${resultForm.clubName.trim()}" does not exist yet. Create it now?`
      );
      if (!shouldCreate) {
        setResultError("Please select an existing club or confirm creation.");
        return;
      }
      try {
        await addClub({ name: resultForm.clubName.trim() });
        // Refresh the local clubs list so it appears in dropdowns immediately
        const updatedClubs = await getClubs();
        setClubs(updatedClubs.map((c: any) => c.name || c));
      } catch (err) {
        setResultError("Failed to create club. Please try again.");
        return;
      }
    }
    // ── End club check ─────────────────────────────────────────────────

    setResultSaving(true); setResultError("");
    try {
      const selectedComp = competitions.find((c) => c.id === selectedCompId);
      const place = resultForm.participated ? null : (resultForm.placeValue !== "" ? Number(resultForm.placeValue) : null);
      const pointsAwarded = resultForm.overridePoints ? Number(resultForm.pointsManual) : autoPoints;

      const payload: Omit<RankingResult, "id" | "createdAt" | "updatedAt"> = {
        competitionId: selectedCompId,
        competitionName: selectedComp?.name ?? "",
        competitionType: selectedComp?.type ?? "",
        athleteId: selectedAthlete.athleteId,
        athleteFirstName: selectedAthlete.firstName,
        athleteLastName: selectedAthlete.lastName,
        clubName: resultForm.clubName.trim(),
        gender: resultForm.gender as any,
        weightClass: resultForm.weightClass,
        ageGroup: resultForm.ageGroup,
        place,
        wins: Number(resultForm.wins),
        pointsAwarded,
        isOverridden: resultForm.overridePoints,
        year: selectedComp?.year ?? new Date().getFullYear(),
        month: selectedComp?.month ?? new Date().getMonth() + 1,
      };

      if (editingResult?.id) { await updateRankingResult(editingResult.id, payload); }
      else { await saveRankingResult(payload); }

      const updated = await getResultsByCompetition(selectedCompId);
      setCompResults(updated);
      setSelectedAthlete(null); setAthleteSearch(""); setResultForm(blankResultForm()); setEditingResult(null);
    } catch (err) {
      console.error("Save result error:", err);
      setResultError("Failed to save result. Check console.");
    } finally { setResultSaving(false); }
  };

  // ── Delete result ────────────────────────────────────────────────────────
  const handleDeleteResult = async (id: string) => {
    if (!window.confirm("Delete this result?")) return;
    // Look up the athleteId so the athlete-results cache is busted too.
    const row = compResults.find((r) => r.id === id);
    await deleteRankingResult(id, row?.athleteId);
    setCompResults(await getResultsByCompetition(selectedCompId));
  };

  // ── Edit result ──────────────────────────────────────────────────────────
  const handleEditResult = (r: RankingResult) => {
    setEditingResult(r);
    setSelectedAthlete({ athleteId: r.athleteId, firstName: r.athleteFirstName, lastName: r.athleteLastName });
    setAthleteSearch(`${r.athleteFirstName} ${r.athleteLastName} (${r.athleteId})`);
    // placeValue: use the stored place directly (it's already a canonical tier value)
    setResultForm({
      gender: r.gender,
      ageGroup: r.ageGroup,
      weightClass: r.weightClass,
      clubName: r.clubName,
      placeValue: r.place ?? "",
      participated: r.place === null,
      wins: r.wins,
      overridePoints: r.isOverridden,
      pointsManual: r.pointsAwarded,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Publish (now goes through a preview modal) ───────────────────────────
  const handleOpenPreview = async () => {
    setPreview(null);
    setPreviewError("");
    setPreviewLoading(true);
    try {
      // 1. All ranking results for the year — same query
      // recalculateAndSaveSnapshots() will run.
      const resSnap = await getDocs(
        query(collection(db, "rankingResults"), where("year", "==", publishYear))
      );
      const results = resSnap.docs.map((d) => d.data() as RankingResult);

      // 2. Unique (gender|ageGroup|weightClass) groups → one snapshot doc each.
      const groupSet = new Set<string>();
      const athleteSet = new Set<string>();
      for (const r of results) {
        groupSet.add(`${r.gender}|${r.ageGroup}|${r.weightClass}`);
        athleteSet.add(r.athleteId);
      }

      // 3. Existing snapshots for the year (to show "will overwrite" count).
      const existingSnap = await getDocs(
        query(collection(db, "rankingSnapshots"), where("year", "==", publishYear))
      );

      // 4. Pull lastUpdated from the most recent existing snapshot (if any).
      let lastTs: Date | null = null;
      for (const d of existingSnap.docs) {
        const u = (d.data() as any).lastUpdated?.toDate?.();
        if (u && (!lastTs || u > lastTs)) lastTs = u;
      }
      // Also check the club ranking snapshot (it stores lastUpdated too).
      try {
        const clubSnap = await getDoc(
          doc(db, "clubRankingSnapshots", `clubs-${publishYear}`)
        );
        const u = (clubSnap.data() as any)?.lastUpdated?.toDate?.();
        if (u && (!lastTs || u > lastTs)) lastTs = u;
      } catch { /* ignore */ }

      setPreview({
        year: publishYear,
        snapshotsToWrite: groupSet.size + 1, // +1 for the club ranking snapshot
        athletesAffected: athleteSet.size,
        resultRows: results.length,
        existingSnapshots: existingSnap.size,
        lastPublishedAt: lastTs ? lastTs.toLocaleString("en-GB") : null,
      });
    } catch (err) {
      console.error("Preview error:", err);
      setPreviewError("Failed to compute preview. Check console.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmPublish = async () => {
    setPublishing(true); setPublishMsg("");
    try {
      await recalculateAndSaveSnapshots(publishYear);
      const ts = new Date().toLocaleString("en-GB");
      setLastPublished(ts);
      setPublishMsg(`✅ Rankings for ${publishYear} published at ${ts}.`);
      setPreview(null);
    } catch (err) {
      console.error("Publish error:", err);
      setPublishMsg("❌ Failed to publish. Check browser console.");
    } finally { setPublishing(false); }
  };

  const handleCancelPreview = () => {
    if (publishing) return; // safety — don't close while writing
    setPreview(null);
    setPreviewError("");
  };

  // ── Derived ──────────────────────────────────────────────────────────────
  const weightClassOptions = getWeightClassesForGroup(resultForm.ageGroup, resultForm.gender);

  const athleteSearchResults = athleteSearch.length >= 2
    ? athletes.filter((a) => {
        const q = athleteSearch.toLowerCase();
        return `${a.firstName} ${a.lastName}`.toLowerCase().includes(q) || (a.athleteId ?? "").toLowerCase().includes(q);
      }).slice(0, 8)
    : [];

  // DOB mismatch warning (non-blocking)
  const ageGroupWarning = dobAgeGroupWarning(selectedAthlete?.dateOfBirth, resultForm.ageGroup);

  // ── Helper to sort weight classes numerically (with + at end) ────────────
  const sortWeightClasses = (weights: string[]): string[] => {
    const getNum = (w: string): number => {
      const match = w.match(/(\d+)/);
      return match ? parseInt(match[0], 10) : 0;
    };
    const isPlus = (w: string): boolean => w.startsWith('+');
    return [...weights].sort((a, b) => {
      const aPlus = isPlus(a);
      const bPlus = isPlus(b);
      if (aPlus !== bPlus) return aPlus ? 1 : -1;
      return getNum(a) - getNum(b);
    });
  };

  // ── Build grouped results for display ────────────────────────────────────
  const getGroupedResults = () => {
    // Order of age groups as defined in AGE_GROUP_DEFINITIONS
    const ageGroupOrder = AGE_GROUP_DEFINITIONS.map(g => g.key);
    // Gender order: male, female, (para groups are separate age groups, but we'll treat gender as male/female only)
    const genderOrder = ["male", "female"];

    // First group by ageGroup -> gender -> weightClass
    const groups: Record<string, Record<string, Record<string, RankingResult[]>>> = {};
    for (const result of compResults) {
      const age = result.ageGroup;
      const gen = result.gender === "female" ? "female" : "male"; // treat para as male/female? But para is separate age group, so fine.
      const wc = result.weightClass;
      if (!groups[age]) groups[age] = {};
      if (!groups[age][gen]) groups[age][gen] = {};
      if (!groups[age][gen][wc]) groups[age][gen][wc] = [];
      groups[age][gen][wc].push(result);
    }

    // Build an array of sections in correct order
    const sections: { ageGroup: string; gender: string; weightClass: string; results: RankingResult[] }[] = [];
    for (const age of ageGroupOrder) {
      if (!groups[age]) continue;
      for (const gender of genderOrder) {
        if (!groups[age][gender]) continue;
        const weightClasses = sortWeightClasses(Object.keys(groups[age][gender]));
        for (const wc of weightClasses) {
          const resultsArray = groups[age][gender][wc];
          // Apply search filter (client-side, by name or ID)
          const filteredArray = resultsSearch.trim()
            ? resultsArray.filter((r) => {
                const q = resultsSearch.toLowerCase();
                return `${r.athleteFirstName} ${r.athleteLastName}`.toLowerCase().includes(q)
                  || (r.athleteId ?? "").toLowerCase().includes(q);
              })
            : resultsArray;
          if (filteredArray.length === 0) continue; // skip groups with no matches
          // Sort results inside group: points desc, then last name + first name
          const sortedResults = [...filteredArray].sort((a, b) => {
            if (b.pointsAwarded !== a.pointsAwarded) return b.pointsAwarded - a.pointsAwarded;
            const nameA = `${a.athleteLastName} ${a.athleteFirstName}`;
            const nameB = `${b.athleteLastName} ${b.athleteFirstName}`;
            return nameA.localeCompare(nameB);
          });
          sections.push({ ageGroup: age, gender, weightClass: wc, results: sortedResults });
        }
      }
    }
    return sections;
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={s.page}>
      <Navbar />
      <div style={{ ...s.container, padding: isMobile ? "16px 12px" : "40px 24px" }}>

        <div style={{ ...s.header, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
          <div><div style={s.pageLabel}>{t("admin.rankings.pageLabel")}</div>
          <div style={{ ...s.heading, fontSize: isMobile ? "28px" : "40px" }}>{t("admin.rankings.title")}</div>
          <div style={s.subheading}>{t("admin.rankings.subtitle")}</div></div>
          <Link to="/admin/rankings/workflow" style={{ ...s.addBtn, textDecoration: "none", display: "inline-block" }}>{t("rankingWorkflow.open")}</Link>
        </div>

        {/* Tabs */}
        <div style={s.tabs}>
          {(["competitions", "results", "publish"] as const).map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              style={{ ...s.tab, ...(activeTab === tab ? s.tabActive : {}) }}>
              {tab === "competitions" ? t("admin.rankings.tab.competitions") : tab === "results" ? t("admin.rankings.tab.results") : t("admin.rankings.tab.publish")}
            </button>
          ))}
        </div>

        {/* ── TAB 1: Competitions ──────────────────────────────────────────── */}
        {activeTab === "competitions" && (
          <div>
            <div style={s.rowBetween}>
              <div style={s.countBadge}>{competitions.length} {t("admin.rankings.competitions.count")}</div>
              {/* "+ Add Competition" removed — all competitions are created from Admin → Competitions */}
            </div>

            {/* Search bar */}
            <div style={{ marginBottom: 16 }}>
              <input
                type="text"
                placeholder={t("admin.allRegistrations.search")}
                value={compSearch}
                onChange={(e) => setCompSearch(e.target.value)}
                style={{
                  width: "100%",
                  maxWidth: 400,
                  padding: "9px 12px",
                  fontSize: 13,
                  borderRadius: 8,
                  border: `1px solid ${C.border}`,
                  backgroundColor: C.surface,
                  color: C.text,
                  outline: "none",
                  fontFamily: FONT.body,
                  boxSizing: "border-box",
                }}
              />
            </div>
            {(() => {
              const filtered = compSearch.trim()
                ? competitions.filter((c: any) =>
                    c.name.toLowerCase().includes(compSearch.toLowerCase())
                  )
                : competitions;

              if (loadingComps) return <div style={s.loading}>{t("common.loading")}</div>;
              if (filtered.length === 0 && compSearch.trim())
                return <div style={s.empty}>No ranking competitions match your search.</div>;
              if (filtered.length === 0)
                return <div style={s.empty}>{t("common.noData")}</div>;

              return (
                <div style={s.tableCard}>
                  <table style={s.table}>
                    <thead>
                      <tr style={s.theadRow}>
                        <th style={s.th}>Competition</th>
                        <th style={s.th}>Type</th>
                        <th style={s.th}>Date</th>
                        <th style={s.th}>Location</th>
                        <th style={{ ...s.th, textAlign: "center" }}>1st / Part. / Win</th>
                        <th style={{ ...s.th, textAlign: "right" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((c) => (
                        <tr key={c.id} style={s.tr}>
                          <td style={s.td}>
                            <div style={{ fontWeight: 700, color: C.text }}>{c.name}</div>
                            {c.nameKa && <div style={{ fontSize: "11px", color: C.text3, marginTop: 2 }}>{c.nameKa}</div>}
                          </td>
                          <td style={s.td}><span style={s.typePill}>{COMPETITION_TYPE_LABELS[c.type] ?? c.type}</span></td>
                          <td style={{ ...s.td, whiteSpace: "nowrap" }}>{toDateStr(c.date)}</td>
                          <td style={{ ...s.td, color: C.text2 }}>{c.location || "—"}</td>
                          <td style={{ ...s.td, textAlign: "center" }}>
                            <span style={{ fontWeight: 700, color: C.red }}>{c.totalPoints}</span>
                            <span style={{ color: C.text3 }}> / {c.participationPoints} / {c.pointsPerWin}</span>
                          </td>
                          <td style={{ ...s.td, textAlign: "right" }}>
                            <div style={s.actionRow}>
                              <button style={s.editBtn} onClick={() => { setEditingComp(c); setCompForm({ ...c, dateStr: tsToInputVal(c.date) }); setShowCompModal(true); }}>Edit</button>
                              <button style={s.deleteBtn} onClick={() => setDeleteCompId(c.id)}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        )}

        {/* ── TAB 2: Enter Results ─────────────────────────────────────────── */}
        {activeTab === "results" && (
          <div>
            {/* Step 1 */}
            <div style={s.card}>
              <div style={s.cardAccent} />
              <div style={s.cardBody}>
                <div style={s.cardTitle}>{t("admin.rankings.step1")}</div>

                {/* Searchable competition selector */}
                <div style={{ position: "relative", marginBottom: 12 }}>
                  <input
                    style={s.input}
                    placeholder="Search competition..."
                    value={compSearchResults}
                    onChange={(e) => {
                      setCompSearchResults(e.target.value);
                      // If the user clears the search, also clear selected competition? No, keep selected.
                    }}
                  />
                  {compSearchResults.trim().length > 0 && (
                    <div style={s.dropdown}>
                      {competitions
                        .filter((c) =>
                          c.name.toLowerCase().includes(compSearchResults.toLowerCase())
                        )
                        .slice(0, 8)
                        .map((c) => (
                          <div
                            key={c.id}
                            style={s.dropdownItem}
                            onClick={() => {
                              setSelectedCompId(c.id);
                              setCompSearchResults("");
                            }}
                          >
                            <strong>{c.name}</strong>
                            <span style={{ color: C.text3, marginLeft: 6, fontSize: "11px" }}>
                              {toDateStr(c.date)}
                            </span>
                          </div>
                        ))}
                      {competitions.filter((c) =>
                        c.name.toLowerCase().includes(compSearchResults.toLowerCase())
                      ).length === 0 && (
                        <div style={{ padding: "10px 14px", color: C.text3, fontSize: "12px" }}>
                          No competitions match
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Selected competition badge */}
                {selectedCompId && (() => {
                  const comp = competitions.find((c) => c.id === selectedCompId);
                  return (
                    <div style={s.selectedBadge}>
                      ✅ {comp?.name} ({toDateStr(comp?.date)})
                      <button
                        style={s.clearBtn}
                        onClick={() => {
                          setSelectedCompId("");
                          setCompSearchResults("");
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  );
                })()}
              </div>
            </div>

            {selectedCompId && (
              <div style={s.card}>
                <div style={s.cardAccent} />
                <div style={s.cardBody}>
                  <div style={s.cardTitle}>
                    {editingResult ? t("admin.rankings.step2Edit") : t("admin.rankings.step2")}
                  </div>

                  {/* Athlete search */}
                  <div style={{ ...s.formGroup, marginBottom: 12 }}>
                    <label style={s.label}>{t("admin.rankings.results.searchLabel")}</label>
                    <div style={{ position: "relative" }}>
                      <input style={s.input} placeholder={t("admin.rankings.results.searchPlaceholder")}
                        value={athleteSearch}
                        onChange={(e) => { setAthleteSearch(e.target.value); setSelectedAthlete(null); }} />
                      {athleteSearchResults.length > 0 && !selectedAthlete && (
                        <div style={s.dropdown}>
                          {athleteSearchResults.map((a) => (
                            <div key={a.id || a.athleteId} style={s.dropdownItem}
                              onClick={() => {
                                setSelectedAthlete(a);
                                setAthleteSearch(`${a.firstName} ${a.lastName} (${a.athleteId})`);
                                if (a.club) setResultForm((prev) => ({ ...prev, clubName: a.club }));
                              }}>
                              <strong>{a.firstName} {a.lastName}</strong>
                              <span style={{ color: C.text3, marginLeft: 6, fontSize: "11px" }}>{a.athleteId}</span>
                              {a.club && <span style={{ color: C.text3, marginLeft: 6, fontSize: "11px" }}>· {a.club}</span>}
                              {!a.dateOfBirth && <span style={{ color: C.warning, marginLeft: 6, fontSize: "10px" }}>no DOB</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {selectedAthlete && (
                      <div style={s.selectedBadge}>
                        ✅ {selectedAthlete.firstName} {selectedAthlete.lastName} ({selectedAthlete.athleteId})
                        {!selectedAthlete.dateOfBirth && (
                          <span style={{ fontSize: "11px", color: C.warning, marginLeft: 8 }}>⚠️ No DOB — select age group manually</span>
                        )}
                        <button style={s.clearBtn} onClick={() => { setSelectedAthlete(null); setAthleteSearch(""); }}>✕</button>
                      </div>
                    )}
                  </div>

                  {/* DOB / age group mismatch warning */}
                  {ageGroupWarning && (
                    <div style={{ ...s.warningBox, marginBottom: 12 }}>{ageGroupWarning}</div>
                  )}

                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: "12px", marginBottom: 12 }}>
                    {/* Gender */}
                    <div style={s.formGroup}>
                      <label style={s.label}>{t("admin.rankings.results.genderLabel")}</label>
                      <select style={s.select} value={resultForm.gender}
                        onChange={(e) => setResultForm((prev) => ({ ...prev, gender: e.target.value }))}>
                        {GENDER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey)}</option>)}
                      </select>
                    </div>

                    {/* Age group — full list from AGE_GROUP_DEFINITIONS */}
                    <div style={s.formGroup}>
                      <label style={s.label}>{t("admin.rankings.results.ageGroupLabel")}</label>
                      <select style={s.select} value={resultForm.ageGroup}
                        onChange={(e) => setResultForm((prev) => ({ ...prev, ageGroup: e.target.value }))}>
                        {AGE_GROUP_DEFINITIONS.map((g) => (
                          <option key={g.key} value={g.key}>
                            {g.label}{g.birthYearRange ? ` (${g.birthYearRange})` : ""}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Weight class — dynamically filtered by ageGroup + gender */}
                    <div style={s.formGroup}>
                      <label style={s.label}>{t("admin.rankings.results.weightClassLabel")}</label>
                      <select style={s.select} value={resultForm.weightClass}
                        onChange={(e) => setResultForm((prev) => ({ ...prev, weightClass: e.target.value }))}>
                        {weightClassOptions.map((wc) => <option key={wc} value={wc}>{wc}</option>)}
                      </select>
                    </div>

                    {/* Club — dropdown + free-text toggle */}
                    <div style={s.formGroup}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <label style={s.label}>{t("admin.rankings.results.clubLabel")}</label>
                        <button type="button"
                          style={{ background: "none", border: "none", fontSize: "11px", color: C.red, cursor: "pointer", padding: 0 }}
                          onClick={() => setClubFreeText((v) => !v)}>
                          {clubFreeText ? t("admin.rankings.results.clubTypeDropdown") : t("admin.rankings.results.clubTypeFree")}
                        </button>
                      </div>
                      {clubFreeText || clubs.length === 0 ? (
                        <>
                          <datalist id="club-suggestions">
                            {clubs.map((c) => (
                              <option key={c} value={c} />
                            ))}
                          </datalist>
                          <input
                            style={s.input}
                            placeholder="Club name..."
                            value={resultForm.clubName}
                            list="club-suggestions"
                            onChange={(e) => setResultForm((prev) => ({ ...prev, clubName: e.target.value }))}
                          />
                        </>
                      ) : (
                        <>
                          <select style={s.select} value={resultForm.clubName}
                            onChange={(e) => setResultForm((prev) => ({ ...prev, clubName: e.target.value }))}>
                            <option value="">{t("admin.rankings.results.clubSelectPlaceholder")}</option>
                            {clubs.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </>
                      )}
                    </div>

                    {/* Place — dropdown with ranges + "participated only" checkbox */}
                    <div style={s.formGroup}>
                      <label style={s.label}>{t("admin.rankings.results.placeLabel")}</label>
                      <select style={s.select} value={resultForm.placeValue}
                        disabled={resultForm.participated}
                        onChange={(e) => setResultForm((prev) => ({ ...prev, placeValue: e.target.value }))}>
                        <option value="">{t("admin.rankings.results.placeSelectPlaceholder")}</option>
                        {PLACE_RANGES.map((r) => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                      <label style={{ fontSize: "11px", color: C.text2, display: "flex", gap: 5, alignItems: "center", marginTop: 4, cursor: "pointer" }}>
                        <input type="checkbox" checked={resultForm.participated}
                          onChange={(e) => setResultForm((prev) => ({ ...prev, participated: e.target.checked, placeValue: "" }))} />
                        {t("admin.rankings.results.participatedOnly")}
                      </label>
                    </div>

                    {/* Wins */}
                    <div style={s.formGroup}>
                      <label style={s.label}>{t("admin.rankings.results.winsLabel")}</label>
                      <input style={s.input} type="number" min={0} value={resultForm.wins}
                        onChange={(e) => setResultForm((prev) => ({ ...prev, wins: Number(e.target.value) }))} />
                    </div>

                    {/* Points */}
                    <div style={s.formGroup}>
                      <label style={s.label}>{t("admin.rankings.results.pointsLabel")}</label>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <input
                          style={{ ...s.input, flex: 1, backgroundColor: resultForm.overridePoints ? C.surface : C.surface2 }}
                          type="number" min={0}
                          value={resultForm.overridePoints ? resultForm.pointsManual : autoPoints}
                          disabled={!resultForm.overridePoints}
                          onChange={(e) => setResultForm((prev) => ({ ...prev, pointsManual: Number(e.target.value) }))} />
                        <button style={s.editBtn}
                          onClick={() => setResultForm((prev) => ({ ...prev, overridePoints: !prev.overridePoints, pointsManual: autoPoints }))}>
                          {resultForm.overridePoints ? t("admin.rankings.results.autoBtn") : t("admin.rankings.results.overrideBtn")}
                        </button>
                      </div>
                      <div style={{ fontSize: "11px", color: C.text3, marginTop: 2 }}>
                        Auto: {autoPoints} pts
                      </div>
                    </div>
                  </div>

                  {resultError && <div style={s.errorMsg}>{resultError}</div>}

                  <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button style={s.addBtn} onClick={handleSaveResult} disabled={resultSaving}>
                      {resultSaving ? "..." : editingResult ? t("admin.rankings.results.updateResult") : t("admin.rankings.results.addResult")}
                    </button>
                    {editingResult && (
                      <button style={s.cancelBtn} onClick={() => {
                        setEditingResult(null); setSelectedAthlete(null);
                        setAthleteSearch(""); setResultForm(blankResultForm());
                      }}>{t("admin.rankings.results.cancelEdit")}</button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Results table — GROUPED DISPLAY */}
            {selectedCompId && (
              <div style={{ marginTop: 24 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" as const }}>
  <div style={{ ...s.sectionLabel, marginBottom: 0 }}>
    Results entered ({compResults.length})
  </div>
  <input
    style={{ ...s.input, maxWidth: 260 }}
    placeholder={t("admin.rankings.results.searchPlaceholder")}
    value={resultsSearch}
    onChange={(e) => setResultsSearch(e.target.value)}
  />
</div>
                {loadingResults ? <div style={s.loading}>Loading...</div>
                  : compResults.length === 0 ? <div style={s.empty}>No results yet.</div>
                  : (
                    <div>
                      {getGroupedResults().map((section, idx) => {
                        const ageDef = AGE_GROUP_DEFINITIONS.find(g => g.key === section.ageGroup);
                        const ageLabel = ageDef ? ageDef.label : section.ageGroup;
                        const genderLabel = section.gender === "male" ? "Boys" : "Girls";
                        const headerTitle = `${ageLabel} / ${genderLabel} / ${section.weightClass} (${section.results.length} athlete${section.results.length !== 1 ? "s" : ""})`;
                        return (
                          <div key={`${section.ageGroup}-${section.gender}-${section.weightClass}-${idx}`} style={{ marginBottom: 24 }}>
                            <div style={{
                              backgroundColor: C.surface2,
                              borderLeft: `4px solid ${C.red}`,
                              padding: "8px 14px",
                              borderRadius: "8px",
                              marginBottom: "12px",
                              fontWeight: "bold",
                              fontSize: "14px",
                            }}>
                              {headerTitle}
                            </div>
                            <div style={s.tableCard}>
                              <table style={s.table}>
                                <thead>
                                  <tr style={s.theadRow}>
                                    <th style={s.th}>Athlete</th>
                                    <th style={s.th}>Club</th>
                                    <th style={{ ...s.th, textAlign: "center" }}>Place</th>
                                    <th style={{ ...s.th, textAlign: "center" }}>Wins</th>
                                    <th style={{ ...s.th, textAlign: "right" }}>Points</th>
                                    <th style={{ ...s.th, textAlign: "right" }}>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {section.results.map((r) => (
                                    <tr key={r.id} style={{ ...s.tr, ...(editingResult?.id === r.id ? { backgroundColor: C.warningDim } : {}) }}>
                                      <td style={s.td}>
                                        <div style={{ fontWeight: 700 }}>{r.athleteFirstName} {r.athleteLastName}</div>
                                        <div style={{ fontSize: "11px", color: C.text3 }}>{r.athleteId}</div>
                                      </td>
                                      <td style={{ ...s.td, color: C.text2 }}>{r.clubName || "—"}</td>
                                      <td style={{ ...s.td, textAlign: "center" }}>{placeLabel(r.place)}</td>
                                      <td style={{ ...s.td, textAlign: "center" }}>{r.wins}</td>
                                      <td style={{ ...s.td, textAlign: "right" }}>
                                        <span style={{ ...s.typePill, ...(r.isOverridden ? { background: C.warningDim, color: C.warning } : {}) }}>
                                          {r.pointsAwarded} {r.isOverridden ? "✏️" : ""}
                                        </span>
                                      </td>
                                      <td style={{ ...s.td, textAlign: "right" }}>
                                        <div style={s.actionRow}>
                                          <button style={s.editBtn} onClick={() => r.id && handleEditResult(r)}>Edit</button>
                                          <button style={s.deleteBtn} onClick={() => r.id && handleDeleteResult(r.id)}>Delete</button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
              </div>
            )}
          </div>
        )}

        {/* ── TAB 3: Publish ──────────────────────────────────────────────── */}
        {activeTab === "publish" && (
          <div style={s.card}>
            <div style={s.cardAccent} />
            <div style={s.cardBody}>
              <div style={s.cardTitle}>{t("admin.rankings.publish.title")}</div>
              <div style={{ fontSize: "13px", color: C.text2, marginBottom: 16, lineHeight: 1.6 }}>
                {t("admin.rankings.publish.description")}
              </div>
              <div style={{ ...s.warningBox, marginBottom: 20 }}>
                {t("admin.rankings.publish.warning")}
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>{t("admin.rankings.publish.year")}</label>
                <select style={{ ...s.select, maxWidth: 200 }} value={publishYear}
                  onChange={(e) => setPublishYear(Number(e.target.value))}>
                  {[2024, 2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              {lastPublished && <div style={{ fontSize: "12px", color: C.text3, marginBottom: 12 }}>Last published: {lastPublished}</div>}
              {publishMsg && (
                <div style={{ ...s.warningBox, marginBottom: 16, backgroundColor: publishMsg.startsWith("✅") ? C.successDim : "var(--color-danger-soft)", color: publishMsg.startsWith("✅") ? C.success : C.red, border: `1px solid ${publishMsg.startsWith("✅") ? C.success : C.red}` }}>
                  {publishMsg}
                </div>
              )}
              <button
                style={{ ...s.addBtn, opacity: (publishing || previewLoading) ? 0.7 : 1 }}
                onClick={handleOpenPreview}
                disabled={publishing || previewLoading}
              >
                {previewLoading
                  ? "Computing preview…"
                  : publishing
                  ? t("common.loading")
                  : t("admin.rankings.publish.button")}
              </button>
            </div>
          </div>
        )}

        {/* ── Publish preview modal ─────────────────────────────────────── */}
        {(preview || previewError) && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "20px",
            }}
            onClick={handleCancelPreview}
          >
            <div
              style={{
                backgroundColor: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: "14px",
                width: "100%",
                maxWidth: "480px",
                padding: "22px 24px",
                boxShadow: "0 28px 80px rgba(0,0,0,0.35)",
                maxHeight: "90vh",
                overflowY: "auto",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  fontFamily: FONT.display,
                  fontSize: "20px",
                  color: C.text,
                  marginBottom: "6px",
                  letterSpacing: "0.5px",
                }}
              >
                Publish preview — {publishYear}
              </div>
              <div style={{ fontSize: "12px", color: C.text3, marginBottom: "16px" }}>
                Review what will be written before confirming. The publish
                operation overwrites existing snapshot documents for this year.
              </div>

              {previewError && (
                <div
                  style={{
                    backgroundColor: "var(--color-danger-soft)",
                    color: C.red,
                    border: `1px solid ${C.red}`,
                    borderRadius: "8px",
                    padding: "10px 14px",
                    fontSize: "13px",
                    marginBottom: "16px",
                  }}
                >
                  {previewError}
                </div>
              )}

              {preview && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    rowGap: "10px",
                    columnGap: "16px",
                    backgroundColor: C.surface2,
                    border: `1px solid ${C.border}`,
                    borderRadius: "10px",
                    padding: "14px 16px",
                    fontSize: "13px",
                    marginBottom: "16px",
                  }}
                >
                  <div style={{ color: C.text2 }}>Ranking result rows scanned</div>
                  <div style={{ color: C.text, fontWeight: 700, textAlign: "right" }}>
                    {preview.resultRows}
                  </div>
                  <div style={{ color: C.text2 }}>Snapshot documents to write</div>
                  <div style={{ color: C.text, fontWeight: 700, textAlign: "right" }}>
                    {preview.snapshotsToWrite}
                  </div>
                  <div style={{ color: C.text2 }}>Athletes affected</div>
                  <div style={{ color: C.text, fontWeight: 700, textAlign: "right" }}>
                    {preview.athletesAffected}
                  </div>
                  <div style={{ color: C.text2 }}>Existing snapshots (will be overwritten)</div>
                  <div
                    style={{
                      color: preview.existingSnapshots > 0 ? C.red : C.text3,
                      fontWeight: 700,
                      textAlign: "right",
                    }}
                  >
                    {preview.existingSnapshots}
                  </div>
                  <div style={{ color: C.text2 }}>Last published</div>
                  <div
                    style={{
                      color: C.text2,
                      fontWeight: 600,
                      textAlign: "right",
                      fontSize: "12px",
                    }}
                  >
                    {preview.lastPublishedAt ?? "Never"}
                  </div>
                </div>
              )}

              {preview && preview.resultRows === 0 && (
                <div
                  style={{
                    backgroundColor: C.warningDim,
                    border: `1px solid ${C.warningBorder}`,
                    borderRadius: "8px",
                    padding: "10px 14px",
                    fontSize: "13px",
                    color: "var(--color-warning)",
                    marginBottom: "16px",
                  }}
                >
                  ⚠ No ranking results exist for {publishYear}. Publishing now
                  will produce empty snapshots.
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", flexWrap: "wrap" }}>
                <button
                  style={{
                    backgroundColor: C.surface2,
                    color: C.text2,
                    border: `1px solid ${C.border2}`,
                    borderRadius: "7px",
                    padding: "10px 18px",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: publishing ? "not-allowed" : "pointer",
                    minHeight: "36px",
                  }}
                  onClick={handleCancelPreview}
                  disabled={publishing}
                >
                  Cancel
                </button>
                <button
                  style={{
                    backgroundColor: C.red,
                    color: "#fff",
                    border: "none",
                    borderRadius: "7px",
                    padding: "10px 20px",
                    fontSize: "13px",
                    fontWeight: 700,
                    cursor: publishing || !preview ? "not-allowed" : "pointer",
                    opacity: publishing || !preview ? 0.7 : 1,
                    minHeight: "36px",
                  }}
                  onClick={handleConfirmPublish}
                  disabled={publishing || !preview}
                >
                  {publishing ? "Publishing…" : "Confirm publish"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Competition modal ──────────────────────────────────────────────── */}
      {showCompModal && (
        <div style={s.overlay}>
          <div style={{ ...s.modal, width: isMobile ? "95vw" : 560 }}>
            <div style={s.modalHeader}>
              <div style={s.modalTitle}>{editingComp ? t("admin.rankings.competition.save") : t("admin.rankings.addCompetition")}</div>
              <button style={s.closeBtn} onClick={() => setShowCompModal(false)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formGroup}>
                <label style={s.label}>{t("admin.rankings.competition.nameEn")} *</label>
                <input style={s.input} value={compForm.name ?? ""} onChange={(e) => setCompForm((p) => ({ ...p, name: e.target.value }))} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>{t("admin.rankings.competition.nameKa")}</label>
                <input style={s.input} value={compForm.nameKa ?? ""} onChange={(e) => setCompForm((p) => ({ ...p, nameKa: e.target.value }))} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>{t("admin.rankings.competition.type")} *</label>
                <select style={s.select} value={compForm.type ?? "georgian_championship"} onChange={(e) => handleCompTypeChange(e.target.value)}>
                  {COMP_TYPES.map((tp) => <option key={tp} value={tp}>{COMPETITION_TYPE_LABELS[tp]}</option>)}
                </select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div style={s.formGroup}>
                  <label style={s.label}>{t("admin.rankings.competition.date")} *</label>
                  <input style={s.input} type="date" value={compForm.dateStr ?? ""} onChange={(e) => setCompForm((p) => ({ ...p, dateStr: e.target.value }))} />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>{t("admin.rankings.competition.location")}</label>
                  <input style={s.input} value={compForm.location ?? ""} onChange={(e) => setCompForm((p) => ({ ...p, location: e.target.value }))} />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>{t("admin.rankings.competition.maxPoints")}</label>
                  <input style={s.input} type="number" value={compForm.totalPoints ?? 0} onChange={(e) => setCompForm((p) => ({ ...p, totalPoints: Number(e.target.value) }))} />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>{t("admin.rankings.competition.participationPoints")}</label>
                  <input style={s.input} type="number" value={compForm.participationPoints ?? 0} onChange={(e) => setCompForm((p) => ({ ...p, participationPoints: Number(e.target.value) }))} />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>{t("admin.rankings.competition.pointsPerWin")}</label>
                  <input style={s.input} type="number" value={compForm.pointsPerWin ?? 0} onChange={(e) => setCompForm((p) => ({ ...p, pointsPerWin: Number(e.target.value) }))} />
                </div>
              </div>
              {compError && <div style={s.errorMsg}>{compError}</div>}
            </div>
            <div style={s.modalFooter}>
              <button style={s.cancelBtn} onClick={() => setShowCompModal(false)}>{t("admin.rankings.competition.cancel")}</button>
              <button style={s.addBtn} onClick={handleSaveComp} disabled={compSaving}>
                {compSaving ? t("common.loading") : editingComp ? t("admin.rankings.competition.save") : t("admin.rankings.competition.create")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirm ─────────────────────────────────────────────────── */}
      {deleteCompId && (
        <div style={s.overlay}>
          <div style={{ ...s.modal, width: 360 }}>
            <div style={s.modalBody}>
              <div style={{ fontSize: "16px", fontWeight: 700, marginBottom: 8 }}>Delete Competition</div>
              <div style={{ fontSize: "13px", color: C.text2, marginBottom: 20 }}>
                This will delete the ranking competition. Results will remain but won't be linked to this competition.
              </div>
              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                <button style={s.cancelBtn} onClick={() => setDeleteCompId(null)}>Cancel</button>
                <button style={{ ...s.deleteBtn, padding: "8px 16px" }} onClick={async () => {
                  await deleteRankingCompetition(deleteCompId);
                  setDeleteCompId(null);
                  loadComps();
                }}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Styles ───────────────────────────────────────────────────────────────────
const s: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", backgroundColor: "transparent" },
  container: { maxWidth: "1200px", margin: "0 auto" },
  header: { marginBottom: 24 },
  pageLabel: { fontSize: "10px", fontWeight: 700, letterSpacing: "2px", color: C.red, marginBottom: 4 },
  heading: { fontFamily: FONT.display, letterSpacing: "2px", color: C.text, lineHeight: 1, marginBottom: 6 },
  subheading: { fontSize: "13px", color: C.text2 },
  tabs: { display: "flex", gap: 4, marginBottom: 24, borderBottom: `2px solid ${C.border}` },
  tab: { padding: "10px 20px", border: "none", borderRadius: "6px 6px 0 0", cursor: "pointer", fontSize: "13px", fontWeight: 600, backgroundColor: "transparent", color: C.text2 },
  tabActive: { backgroundColor: C.red, color: "#fff" },
  rowBetween: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  countBadge: { fontSize: "12px", color: C.text3 },
  addBtn: { backgroundColor: C.red, color: "#fff", border: "none", borderRadius: 8, padding: "9px 20px", fontWeight: 700, cursor: "pointer", fontSize: "13px" },
  editBtn: { backgroundColor: C.surface2, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: "5px 12px", cursor: "pointer", fontSize: "12px", fontWeight: 600 },
  deleteBtn: { backgroundColor: C.dangerDim, color: C.danger, border: `1px solid ${C.dangerBorder}`, borderRadius: 6, padding: "5px 12px", cursor: "pointer", fontSize: "12px", fontWeight: 600 },
  cancelBtn: { backgroundColor: C.surface2, color: C.text2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: "13px" },
  loading: { padding: 32, textAlign: "center", color: C.text3 },
  empty: { padding: 32, textAlign: "center", color: C.text3, fontSize: "14px" },
  tableCard: { backgroundColor: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "auto" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: "700px" },
  theadRow: { borderBottom: `2px solid ${C.border}`, backgroundColor: C.surface2 },
  th: { textAlign: "left", padding: "11px 14px", fontSize: "10px", fontWeight: 700, letterSpacing: "1px", color: C.text3, textTransform: "uppercase", whiteSpace: "nowrap" },
  tr: { borderBottom: `1px solid ${C.border}` },
  td: { padding: "13px 14px", fontSize: "13px", color: C.text, verticalAlign: "middle" },
  typePill: { display: "inline-block", background: C.redDim, color: C.red, borderRadius: 20, padding: "2px 10px", fontSize: "11px", fontWeight: 700, whiteSpace: "nowrap" },
  actionRow: { display: "flex", gap: 6, justifyContent: "flex-end" },
  sectionLabel: { fontSize: "11px", fontWeight: 700, letterSpacing: "1.5px", color: C.text2, textTransform: "uppercase", marginBottom: 12 },
  card: { backgroundColor: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "visible", marginBottom: 20 },
  cardAccent: { height: "3px", background: C.red, borderRadius: "12px 12px 0 0" },
  cardBody: { padding: "20px 24px" },
  cardTitle: { fontFamily: FONT.display, fontSize: "22px", letterSpacing: "1px", color: C.text, marginBottom: 16 },
  formGroup: { display: "flex", flexDirection: "column" as const, gap: 4, marginBottom: 0 },
  label: { fontSize: "11px", fontWeight: 700, letterSpacing: "0.8px", color: C.text2, textTransform: "uppercase" },
  input: { padding: "9px 12px", fontSize: "13px", borderRadius: 8, border: `1px solid ${C.border}`, backgroundColor: C.surface, color: C.text, outline: "none", width: "100%", boxSizing: "border-box" as const },
  select: { padding: "9px 12px", fontSize: "13px", borderRadius: 8, border: `1px solid ${C.border}`, backgroundColor: C.surface, color: C.text, outline: "none", width: "100%", boxSizing: "border-box" as const },
  dropdown: { position: "absolute" as const, zIndex: 200, backgroundColor: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", maxHeight: 220, overflowY: "auto" as const, top: "calc(100% + 2px)", left: 0, right: 0 },
  dropdownItem: { padding: "10px 14px", cursor: "pointer", fontSize: "13px", borderBottom: `1px solid ${C.border}` },
  selectedBadge: { marginTop: 6, padding: "8px 12px", backgroundColor: C.successDim, borderRadius: 8, fontSize: "13px", color: C.success, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 4 },
  clearBtn: { background: "none", border: "none", cursor: "pointer", color: C.text3, fontSize: "14px" },
  errorMsg: { color: C.red, fontSize: "12px", marginTop: 8, padding: "8px 12px", backgroundColor: "var(--color-danger-soft)", borderRadius: 6 },
  warningBox: { color: C.warning, fontSize: "13px", padding: "12px 16px", backgroundColor: C.warningDim, borderRadius: 8, border: `1px solid ${C.warningBorder}` },
  overlay: { position: "fixed" as const, inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { backgroundColor: C.surface, borderRadius: 14, overflow: "hidden", maxHeight: "90vh", display: "flex", flexDirection: "column" as const },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px", borderBottom: `1px solid ${C.border}` },
  modalTitle: { fontFamily: FONT.display, fontSize: "22px", letterSpacing: "1px", color: C.text },
  modalBody: { padding: 24, overflowY: "auto" as const, flex: 1, display: "flex", flexDirection: "column" as const, gap: 14 },
  modalFooter: { padding: "16px 24px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "flex-end", gap: 12 },
  closeBtn: { background: "none", border: "none", cursor: "pointer", fontSize: "18px", color: C.text3 },
};

export default ManageRankings;
